/**
 * Offline sync metadata + durable device id.
 * @see docs/OFFLINE_FIRST_DESIGN.md §1.3
 */

import { get, set, del } from "idb-keyval"
import type { SyncMeta } from "./types"

export const SYNC_META_KEY = "offline:sync:meta"

function newDeviceId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID()
  }
  return `dev-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
}

export async function getSyncMeta(): Promise<SyncMeta> {
  const existing = await get<SyncMeta>(SYNC_META_KEY)
  if (existing?.deviceId) {
    return {
      deviceId: existing.deviceId,
      lastSyncAt: existing.lastSyncAt ?? null,
      lastPullCursor: existing.lastPullCursor ?? null,
      lastSuccessfulOpId: existing.lastSuccessfulOpId ?? null,
      schemaVersion: 1,
    }
  }
  const meta: SyncMeta = {
    deviceId: newDeviceId(),
    lastSyncAt: null,
    lastPullCursor: null,
    lastSuccessfulOpId: null,
    schemaVersion: 1,
  }
  await set(SYNC_META_KEY, meta)
  return meta
}

export async function getDeviceId(): Promise<string> {
  const meta = await getSyncMeta()
  return meta.deviceId
}

export async function patchSyncMeta(
  patch: Partial<Omit<SyncMeta, "deviceId" | "schemaVersion">>,
): Promise<SyncMeta> {
  const current = await getSyncMeta()
  const next: SyncMeta = {
    ...current,
    ...patch,
    deviceId: current.deviceId,
    schemaVersion: 1,
  }
  await set(SYNC_META_KEY, next)
  return next
}

export async function clearSyncMeta(): Promise<void> {
  // Shared tablets: clear entirely so next session gets a fresh deviceId
  // (ops cannot be attributed to a prior staff member's offline queue context).
  await del(SYNC_META_KEY)
}
